import {List, Record, Set} from 'immutable';
import NewFilterWord from 'records/admin/topics/NewFilterWord';
import NewFilterWordProfile from 'records/admin/topics/NewFilterWordProfile';

/**
 * Contains all profiles in the current user's account together with any filter words not associated to any profile.
 */
// XXX when upgrading typescript or immutable, double-check the casts here. Some record functions return immutable Maps
// instead of `this` for whatever reason. Or, maybe try to use our Data function to fix these problems.
export default class NewFilterWordProfilesInAccount extends Record({
  profiles: List(),
  unassociatedFilterWords: List(),
}) {
  readonly profiles: List<NewFilterWordProfile>;
  readonly unassociatedFilterWords: List<NewFilterWord>;

  constructor(object = {profiles: [], unassociatedFilterWords: []}) {
    super(
      Object.assign({}, object, {
        profiles: List(object.profiles.map(profile => new NewFilterWordProfile(profile))),
        unassociatedFilterWords: List(
          object.unassociatedFilterWords.map(filterWord => new NewFilterWord(filterWord))
        ),
      })
    );
  }

  addProfile(newProfile: NewFilterWordProfile): NewFilterWordProfilesInAccount {
    return this.updateIn(['profiles'], profiles =>
      profiles.push(newProfile)
    ) as NewFilterWordProfilesInAccount; // annoying, but apparently necessary.
  }

  addUnassociatedFilterWord(newFilterWord: NewFilterWord): NewFilterWordProfilesInAccount {
    return this.updateIn(['unassociatedFilterWords'], words =>
      words.push(newFilterWord)
    ) as NewFilterWordProfilesInAccount; // annoying, but apparently necessary.
  }

  /**
   * Get all filter words (topics AND keywords), including those not associated with any profile.
   */
  filterWords(): List<NewFilterWord> {
    const flattened = this.profiles
      .map(profile => profile.filterWords)
      // TODO wrong type here for whatever reason w/o cast.
      .flatten(1) as unknown as List<NewFilterWord>;

    return (
      flattened
        .concat(this.unassociatedFilterWords)
        // words can appear more than once, as the same word can be added to multiple profiles. deduplicate.
        .groupBy(fw => fw.filterWordId)
        .map(listOfDuplicates => listOfDuplicates.first())
        .toList()
    );
  }

  updateFilterWord(updatedFilterWord: NewFilterWord): NewFilterWordProfilesInAccount {
    const profiles = this.profiles.map(profile => profile.updateFilterWord(updatedFilterWord));
    const unassociatedFilterWords = this.unassociatedFilterWords.set(
      this.unassociatedFilterWords.findIndex(
        filterWord => filterWord.filterWordId === updatedFilterWord.filterWordId
      ),
      updatedFilterWord
    );
    return this.merge({profiles, unassociatedFilterWords});
  }

  updateProfile(updatedProfile: NewFilterWordProfile): NewFilterWordProfilesInAccount {
    const index = this.profiles.findIndex(
      profile => profile.filterWordProfileId === updatedProfile.filterWordProfileId
    );
    const withUpdatedProfile = this.setIn(['profiles', index], updatedProfile);

    // Since it is not possible to delete a filter word simply by removing it from one or more
    // profiles, we can conclude that filter words removed that are not part of another profile
    // have become unassociated, and must therefore be added to the unassociated list.
    const newFilterWords = Set(
      withUpdatedProfile.filterWords().map(filterWord => filterWord.filterWordId)
    );
    const newlyUnassociatedFilterWords = this.profiles
      .get(index)
      .filterWords.filterNot(filterWord => newFilterWords.has(filterWord.filterWordId));

    return withUpdatedProfile.updateIn(['unassociatedFilterWords'], ufw =>
      ufw.concat(newlyUnassociatedFilterWords)
    ) as NewFilterWordProfilesInAccount; // annoying, but apparently necessary.
  }
}
